/**
 * LGPD Block E — Google Analytics wrapper.
 *
 * GA is injected ONLY through the Block B consent-gated loader
 * (`loadAnalyticsScript`), which itself refuses to inject anything without a
 * stored analytics opt-in. On top of that, this wrapper additionally requires
 * `VITE_GA_ENABLED=true` and a measurement id (see `gaShouldLoad`). There is no
 * GA `<script>` in index.html and no hardcoded measurement id anywhere.
 *
 * `trackPageView` / `trackEvent` are safe no-ops unless GA is fully allowed, and
 * they sanitize every payload (safe event names + non-PII scalar params only).
 */

import { OBSERVABILITY_ENV } from '@/shared/config/env'
import {
  hasAnalyticsConsent,
  loadAnalyticsScript,
  onConsentChange,
} from '@/shared/consent'
import {
  gaScriptUrl,
  gaShouldLoad,
  isSafeEventName,
  sanitizeGaParams,
  sanitizePagePath,
  type GaConfig,
} from './gaPolicy'

let gaLoaded = false

function gaConfig(): GaConfig {
  return {
    enabled: OBSERVABILITY_ENV.ga.enabled,
    measurementId: OBSERVABILITY_ENV.ga.measurementId,
  }
}

type GtagWindow = {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

function setupGtag(measurementId: string): void {
  const w = window as unknown as GtagWindow
  const dataLayer = (w.dataLayer = w.dataLayer || [])
  if (!w.gtag) {
    w.gtag = function gtag() {
      // GA's canonical snippet pushes the arguments object.
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments)
    }
  }
  const gtag = w.gtag
  gtag('js', new Date())
  // Privacy-preserving config: no ad signals, no ad personalization, IP
  // anonymized, and we send page views explicitly (not automatically).
  gtag('config', measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    send_page_view: false,
  })
}

/** Load GA now if (and only if) it is fully allowed. Returns true if loaded. */
export function loadGaIfAllowed(): boolean {
  if (gaLoaded) return true
  if (typeof document === 'undefined') return false
  const cfg = gaConfig()
  if (!gaShouldLoad(cfg, hasAnalyticsConsent())) return false
  const injected = loadAnalyticsScript(
    'google-analytics',
    gaScriptUrl(cfg.measurementId),
    { onLoad: () => setupGtag(cfg.measurementId) },
  )
  if (injected) gaLoaded = true
  return injected
}

/**
 * Wire GA readiness. No-op unless enabled+consented. Subscribes to consent
 * changes so a LATER analytics opt-in loads GA (an opt-OUT after load requires a
 * page reload — documented). Safe to call once at app startup.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return
  loadGaIfAllowed()
  onConsentChange((consent) => {
    if (consent?.categories.analytics === true) loadGaIfAllowed()
  })
}

function gaReady(): boolean {
  return (
    gaShouldLoad(gaConfig(), hasAnalyticsConsent()) &&
    typeof (window as unknown as GtagWindow).gtag === 'function'
  )
}

/** Track a page view. No-op unless GA is fully allowed and loaded. */
export function trackPageView(path?: string): void {
  if (typeof window === 'undefined' || !gaReady()) return
  const safePath = sanitizePagePath(path)
  const gtag = (window as unknown as GtagWindow).gtag!
  gtag('event', 'page_view', sanitizeGaParams(safePath ? { page_path: safePath } : undefined))
}

/** Track a custom event. No-op unless GA is allowed; sanitizes name + params. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !gaReady()) return
  if (!isSafeEventName(name)) return
  const gtag = (window as unknown as GtagWindow).gtag!
  gtag('event', name, sanitizeGaParams(params))
}
